import { type InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import { SkeletonList } from '@/components/Skeleton'
import { useInfiniteScroll } from '@/hooks/infinite-scroll-hooks'
import { observer } from 'mobx-react-lite'
import { type SkeletonListChildren } from '@/components/Skeleton/SkeletonList'

export interface SkeletonListInfiniteScrollZoneProps {
  /**
   * The Skeleton element to clone.
   */
  children: SkeletonListChildren

  /**
   * The scroll provider.
   */
  scroller: InfiniteScrollProvider<unknown>

  /**
   * If specified, limits the max amount of skeletons to the given number.
   * Does not apply if {@link skeletonCount} is specified.
   */
  maxSkeletons?: number

  /**
   * If specified, uses this exact amount of skeletons rather than one
   * estimated from the scroll provider.
   */
  skeletonCount?: number

  /**
   * If specified, this amount will  delay the change of the 'active' state
   * on the scroller. This can be useful when UI updates for new elements
   * are slow.
   */
  activeDelay?: number

  /**
   * Whether to attach the scroll element ref to the first or last element
   * in the list.
   */
  attachRefTo?: 'first' | 'last' | 'all'

  /**
   * How much of a scroll margin to apply; when non-zero, the scroll will
   * be triggered when the target element is this number of pixels from
   * being in-view.
   *
   * @default 30
   */
  scrollMargin?: number
}

/**
 * An infinite scroll trigger that integrates with the skeleton list
 * component.
 *
 * IMPORTANT: You *MUST* call `useSkeletonListContext()` and apply
 * the `ref` to the skeleton list item in order for the trigger to work.
 */
export const SkeletonListInfiniteScrollTrigger = observer(
  function SkeletonListInfiniteScrollTrigger({
    scroller,
    maxSkeletons,
    skeletonCount,
    children,
    attachRefTo = 'all',
    activeDelay,
    scrollMargin = 30,
  }: SkeletonListInfiniteScrollZoneProps) {
    const { connectRef } = useInfiniteScroll({
      scroller,
      activeDelay,
      // Trigger the load slightly before the skeleton enters the viewport.
      margin:
        attachRefTo === 'first'
          ? `0px 0px ${scrollMargin}px 0px`
          : attachRefTo === 'last'
            ? `${scrollMargin}px 0px 0px 0px`
            : `${scrollMargin}px 0px ${scrollMargin}px 0px`,
    })

    if (scroller.isDone || scroller.isRevalidating) {
      return null
    }

    skeletonCount ??= maxSkeletons
      ? Math.min(estimatedRemainingItemsToLoad(scroller), maxSkeletons)
      : estimatedRemainingItemsToLoad(scroller)

    return (
      <SkeletonList
        attachRefTo={attachRefTo}
        count={skeletonCount}
        ref={connectRef}
      >
        {children}
      </SkeletonList>
    )
  },
)

/**
 * Estimates the amount of items left to load from the scroller.
 * @param scroller
 */
function estimatedRemainingItemsToLoad(
  scroller: InfiniteScrollProvider<unknown>,
) {
  if (scroller.isDone) {
    return 0
  }

  if (scroller.total === 0) {
    return scroller.limit
  }

  return Math.max(0, scroller.total - scroller.items.length)
}
